// Considered: none — feed entries today are `<span onClick>`/`<button>`s with an unused onClick
// (see specs/stream-trajectory-tools.md Problem Statement). New behavior: peek + inspect +
// propagation control as one contract, so every future inspectable is keyboard-reachable by
// construction instead of by convention. Composes PeekAtom. See spec Reuse Audit.
//
// Source: design/metaspace-tools/metaspace-interactions.jsx `MSInspectable`.

import React from 'react';
import PeekAtom from '../../atoms/Peek/PeekAtom';
import type { InspectorPayload } from 'types/inspector';
import './InspectableMolecule.scss';

export interface InspectableMoleculeProps {
  /** The payload handed to `onInspect` on activation. No payload ⇒ nothing to inspect. */
  payload?: InspectorPayload;
  onInspect?: (payload: InspectorPayload) => void;
  /** Rich hover content, rendered via PeekAtom. Never triggers `onInspect`. */
  peek?: React.ReactNode;
  children: React.ReactNode;
  block?: boolean;
  grow?: number;
  className?: string;
  'aria-label'?: string;
}

const InspectableMolecule: React.FC<InspectableMoleculeProps> = ({
  payload,
  onInspect,
  peek,
  children,
  block,
  grow,
  className,
  'aria-label': ariaLabel,
}) => {
  const canInspect = Boolean(onInspect && payload);

  const buttonClasses = [
    'inspectable-molecule',
    'focus-ring',
    block ? 'inspectable-molecule--block' : '',
    className || '',
  ]
    .filter(Boolean)
    .join(' ');

  // A real <button>, not a <span onClick> — keyboard-reachable by construction (Enter/Space are
  // native), and it carries an accessible name via aria-label when the children don't speak for
  // themselves.
  const node = canInspect ? (
    <button
      type="button"
      className={buttonClasses}
      style={grow ? { flex: `${grow} 1 0%`, minWidth: 0 } : undefined}
      aria-label={ariaLabel}
      onClick={(e) => {
        e.stopPropagation();
        if (payload) onInspect?.(payload);
      }}
    >
      {children}
    </button>
  ) : (
    <span
      className={buttonClasses}
      style={grow ? { flex: `${grow} 1 0%`, minWidth: 0 } : undefined}
    >
      {children}
    </span>
  );

  return peek ? (
    <PeekAtom peek={peek} block={block} grow={grow}>
      {node}
    </PeekAtom>
  ) : (
    node
  );
};

InspectableMolecule.displayName = 'InspectableMolecule';

export default InspectableMolecule;
